import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Send } from 'lucide-react';
import { fmtMoney as fmt } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getPeriodo() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const inicio = new Date(y, m, 1).toISOString().split('T')[0];
  const fin = new Date(y, m + 1, 0).toISOString().split('T')[0];
  return { inicio, fin };
}

export function MiNomina() {
  const navigate = useNavigate();
  const periodo = getPeriodo();
  const [fechaInicio, setFechaInicio] = useState(periodo.inicio);
  const [fechaFin, setFechaFin] = useState(periodo.fin);
  const [nomina, setNomina] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aclaracionTexto, setAclaracionTexto] = useState('');
  const [aclaracionCorteId, setAclaracionCorteId] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const loadNomina = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/nomina/mia?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setNomina(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { loadNomina(); }, [loadNomina]);

  const handleEnviarAclaracion = async () => {
    if (!aclaracionTexto.trim()) return;
    setEnviando(true);
    try {
      await axios.post(`${API}/nomina/aclaracion`, {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        corte_id: aclaracionCorteId,
        texto: aclaracionTexto,
      });
      setAclaracionTexto('');
      setAclaracionCorteId(null);
      setEnviado(true);
      loadNomina();
      setTimeout(() => setEnviado(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Mi Nómina</h1>
            <p className="text-sm text-zinc-500">Consulta tus percepciones y deducciones</p>
          </div>
        </div>
      </div>

      {/* Periodo */}
      <div className="px-4 py-3 bg-white border-b border-zinc-200">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs font-medium text-zinc-500 mb-1 block">Desde</label>
            <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
              className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-zinc-500 mb-1 block">Hasta</label>
            <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
              className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </div>
          <button onClick={loadNomina} className="h-10 px-4 bg-zinc-900 text-white rounded-lg text-sm font-medium">
            Ver
          </button>
        </div>
      </div>

      <div className="flex-1 px-4 py-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <span className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : !nomina ? null : (
          <>
            {/* Resumen */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
              <h2 className="font-bold text-zinc-900">{nomina.nombre}</h2>

              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Sueldo diario</span>
                <span className="font-medium">{fmt(nomina.sueldo_diario)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Días trabajados</span>
                <span className="font-medium">{nomina.dias_trabajados}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-zinc-100 pt-2">
                <span className="text-zinc-700 font-medium">Percepción base</span>
                <span className="text-green-600 font-bold">{fmt(nomina.percepcion_base)}</span>
              </div>
            </div>

            {/* Deducciones corte */}
            {nomina.deducciones_corte.length > 0 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-2">
                <h3 className="text-sm font-medium text-zinc-500">DEDUCCIONES POR CORTE</h3>
                {nomina.deducciones_corte.map(d => (
                  <div key={d.corte_id} className="py-2 border-b border-zinc-100 last:border-0">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-700">{d.concepto}</span>
                      <span className="text-red-600 font-bold">-{fmt(d.monto)}</span>
                    </div>
                    {d.modificado && <p className="text-xs text-amber-600 mt-0.5">Monto ajustado por administrador</p>}
                    <button
                      onClick={() => setAclaracionCorteId(d.corte_id)}
                      className={`mt-1 text-xs font-medium ${aclaracionCorteId === d.corte_id ? 'text-zinc-400' : 'text-blue-600'}`}
                    >
                      {aclaracionCorteId === d.corte_id ? 'Seleccionado para aclaración' : '+ Agregar aclaración'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Ajustes manuales */}
            {nomina.ajustes.length > 0 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-2">
                <h3 className="text-sm font-medium text-zinc-500">AJUSTES</h3>
                {nomina.ajustes.map(a => (
                  <div key={a.id} className="flex justify-between items-center py-1.5 border-b border-zinc-100 last:border-0">
                    <span className="text-sm text-zinc-700">{a.concepto}</span>
                    <span className={`font-bold ${a.tipo === 'percepcion' ? 'text-green-600' : 'text-red-600'}`}>
                      {a.tipo === 'percepcion' ? '+' : '-'}{fmt(a.monto)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Total */}
            <div className={`rounded-xl p-4 flex justify-between items-center ${nomina.total_neto >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <span className="font-bold text-zinc-900 text-lg">Total a recibir</span>
              <span className={`text-2xl font-bold ${nomina.total_neto >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {fmt(nomina.total_neto)}
              </span>
            </div>

            {/* Aclaración */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
              <h3 className="font-medium text-zinc-900">Enviar aclaración</h3>
              <p className="text-xs text-zinc-500">
                Si tienes alguna duda sobre tu nómina, escríbela aquí. El administrador la revisará antes del día de pago.
                {aclaracionCorteId && <span className="text-blue-600 ml-1">(Referente a una deducción de corte)</span>}
              </p>
              <textarea
                placeholder="Describe tu aclaración..."
                value={aclaracionTexto}
                onChange={e => setAclaracionTexto(e.target.value)}
                className="w-full h-24 px-3 py-2 border border-zinc-300 rounded-lg text-sm resize-none"
              />
              {aclaracionCorteId && (
                <button onClick={() => setAclaracionCorteId(null)} className="text-xs text-zinc-400">
                  ✕ Quitar referencia al corte
                </button>
              )}
              {enviado && <p className="text-sm text-green-600 font-medium">Aclaración enviada correctamente</p>}
              <button
                onClick={handleEnviarAclaracion}
                disabled={enviando || !aclaracionTexto.trim()}
                className="w-full h-11 bg-zinc-900 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {enviando ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar aclaración
              </button>
            </div>

            {/* Aclaraciones previas */}
            {nomina.aclaraciones.length > 0 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
                <h3 className="text-sm font-medium text-zinc-500">MIS ACLARACIONES</h3>
                {nomina.aclaraciones.map(a => (
                  <div key={a.id} className="py-2 border-b border-zinc-100 last:border-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.status === 'pendiente' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {a.status === 'pendiente' ? 'Pendiente' : 'Atendida'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-700">{a.texto}</p>
                    {a.respuesta_admin && (
                      <div className="mt-2 pl-3 border-l-2 border-zinc-300">
                        <p className="text-xs text-zinc-500 font-medium">Respuesta del administrador:</p>
                        <p className="text-sm text-zinc-700">{a.respuesta_admin}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
